import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NavBarItem, SideBarItem } from '../../../../shared/types/NavBarItem';
import { useDeviceType } from '../../../../utilities/common/deviceType';

import Chevron from '../../../../assets/icons/General/chevron.svg?react';
import useTracking from '../../../../utilities/hooks/useTracking';
import {
  EventActions,
  EventNames,
} from '../../../../shared/constants/analytics';
import { SideBarApi } from '../../../../shared/service/sideBar';
import useFetch from '../../../../utilities/hooks/useFetch';
import { twMerge } from 'tailwind-merge';

export interface INavbarProps {
  setIsSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarCollapsed?: boolean;
}

function Navbar({
  setIsSidebarOpen,
  isSidebarCollapsed = false,
}: INavbarProps) {
  const { trackEvent } = useTracking();
  const [isDropdown, setIsDropdown] = useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [navBarItems, setNavBarItems] = useState<NavBarItem[]>([]);

  const navigate = useNavigate();
  const route = useLocation().pathname;
  const isMobile = useDeviceType();

  const isDimension = '/d/dimensions';

  // Throttle navigation to prevent rapid sidebar clicks from triggering
  // race conditions between layout and widget API calls (SEL-6659)
  const NAVIGATION_COOLDOWN_MS = 1000;
  const lastNavigationTime = useRef<number>(0);
  const isNavigationThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastNavigationTime.current < NAVIGATION_COOLDOWN_MS) {
      return true;
    }

    lastNavigationTime.current = now;

    return false;
  }, []);

  const handleNavBarItemClick = (navBarItem: NavBarItem) => {
    if (navBarItem.dropdownItems) {
      setIsDropdown(!isDropdown);

      const eventLabel = navBarItem.label.replace(/[\s-]/g, '_').toLowerCase();
      trackEvent(EventNames.WellBeingDashboard, {
        eventLabel,
        eventAction: EventActions.click,
        eventCategory: eventLabel,
        eventValue: eventLabel,
        eventSource: 'side_bar',
      });
    } else {
      navigate(navBarItem.route);
      isMobile && setIsSidebarOpen && setIsSidebarOpen(false);
    }
  };

  const { isLoading } = useFetch<SideBarItem[]>(
    ['SideBarApi'],
    () => SideBarApi(),
    {
      onSuccess: (res) => {
        const transformedItems: NavBarItem[] = res.map((item) => ({
          id: item.id,
          label: item.label,
          iconBgColor: item.meta.color || 'transparent',
          route:
            item.slug &&
            (item.slug.includes('resourcehub')
              ? `/${item.slug}`
              : `/d/${item.slug}`),
          icons: item.icon,
          dropdownItems: item.children
            ? item.children.map((child) => ({
                id: child.id,
                label: child.label,
                route: child.slug && `/d/${child.slug}`,
                icons: child.icon,
                iconBgColor: child.meta.color || 'transparent',
              }))
            : undefined,
        }));
        setNavBarItems(transformedItems);
      },
      onError: () => {},
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    const isInDropdown = navBarItems.some((item) =>
      item.dropdownItems?.some((dropdownItem) => dropdownItem.route === route),
    );
    setIsDropdown(isInDropdown);
  }, [route, navBarItems]);

  return (
    <nav className="relative z-10 grid grid-cols-1 gap-y-4">
      {isLoading
        ? Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-black-200 h-10 w-[200px] animate-pulse rounded-md"
            ></div>
          ))
        : navBarItems.map((navBarItem) => {
            const isSelected =
              route === navBarItem.route ||
              // If child page of dimensions
              (navBarItem.route === isDimension &&
                route.startsWith('/d/dimensions-') &&
                // Exclude dimensions-legacy
                !route.startsWith('/d/dimensions-legacy'));

            const isActive = isSelected || isHovered;

            const icon = `${import.meta.env.VITE_IMAGE_BASE_URL}uploads/${
              navBarItem.icons
            }${isActive ? '_active.svg' : '_inactive.svg'}`;

            return (
              <React.Fragment key={navBarItem.id}>
                <button
                  type="button"
                  className={`group text-taupe-900 hover:text-black-900 relative flex w-full flex-shrink-0 cursor-pointer flex-row items-center gap-x-3 truncate rounded-full px-3 py-2 text-base leading-6 transition-all duration-200 hover:font-medium active:scale-[0.9] ${
                    isSelected ? 'bg-taupe-500' : 'hover:bg-taupe-500'
                  } ${isSidebarCollapsed ? 'justify-center' : 'justify-start'}`}
                  onClick={() => {
                    if (!navBarItem.dropdownItems && isNavigationThrottled()) {
                      return;
                    }

                    handleNavBarItemClick(navBarItem);
                  }}
                  title={navBarItem.label}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <div
                    className={twMerge([
                      'flex flex-shrink-0 items-center justify-center rounded-full p-[1.25px]',
                      isSidebarCollapsed && 'float-left',
                    ])}
                    style={
                      isSelected
                        ? {
                            backgroundColor: navBarItem.iconBgColor,
                          }
                        : {
                            backgroundColor: 'transparent',
                          }
                    }
                  >
                    <img className="shrink-0" src={icon} alt={icon} />
                  </div>
                  {!isSidebarCollapsed && (
                    <>
                      <span
                        className={`overflow-hidden text-ellipsis ${isSelected ? 'font-semibold text-gray-900' : ''}`}
                      >
                        {navBarItem.label}
                      </span>
                      {navBarItem.dropdownItems && (
                        <Chevron
                          className={`stroke-taupe-900 absolute right-4 transition-transform ease-out ${
                            isDropdown && 'rotate-180'
                          }`}
                        />
                      )}
                    </>
                  )}
                </button>
                {/* //TODO: extract to separate component */}
                {navBarItem.dropdownItems && (
                  <div
                    className={`grid-cols-1 gap-y-4 ${
                      isSidebarCollapsed || isDropdown ? 'grid' : 'hidden'
                    }`}
                  >
                    {navBarItem.dropdownItems.map((item) => {
                      const childIcon = `${
                        import.meta.env.VITE_IMAGE_BASE_URL
                      }uploads/${item.icons}${
                        route === item.route ? '_active.svg' : '_inactive.svg'
                      }`;

                      return (
                        <Link
                          key={item.route}
                          to={item.route}
                          className={`group text-taupe-900 flex flex-row items-center rounded-full px-3 py-2 text-base leading-6 transition-all duration-200 active:scale-[0.9] ${
                            route === item.route
                              ? 'bg-taupe-500 font-semibold text-gray-900'
                              : 'hover:bg-taupe-500 hover:text-black-900 active:bg-black-200 hover:font-medium'
                          } ${isSidebarCollapsed ? 'justify-center' : 'ml-10 justify-start'}`}
                          onClick={(e) => {
                            if (isNavigationThrottled()) {
                              e.preventDefault();
                              return;
                            }
                            handleNavBarItemClick(item);
                          }}
                          title={item.label}
                        >
                          <div
                            className={`flex shrink-0 items-center justify-center rounded-full p-[1.25px] ${isSidebarCollapsed ? 'float-left' : 'mr-3'}`}
                            style={
                              route === item.route
                                ? {
                                    backgroundColor:
                                      item.iconBgColor ||
                                      navBarItem.iconBgColor,
                                  }
                                : {
                                    backgroundColor: 'transparent',
                                  }
                            }
                          >
                            <img
                              className="shrink-0"
                              src={childIcon}
                              alt={childIcon}
                            />
                          </div>
                          {!isSidebarCollapsed && (
                            <span
                              className={`line-clamp-1 overflow-hidden text-ellipsis ${route === item.route ? 'font-semibold text-gray-900' : 'hover:text-black-900 hover:font-medium'}`}
                            >
                              {item.label}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
    </nav>
  );
}

export default Navbar;
